/**
 * Pure helpers shared by the product-update admin editor and its tests.
 * No React, no DOM: everything here is unit-testable with plain node.
 */

/** Number of features a member sees in the popup before scrolling. */
export const FEATURE_FOLD = 5;

/** Hero banner target. Renders at 560 × 315 inside the 560px dialog, 2× for retina. */
export const HERO_IMAGE = {
  width: 1120,
  height: 630,
  ratio: 16 / 9,
  ratioLabel: '16:9',
  maxBytes: 10 * 1024 * 1024,
  acceptedTypes: 'image/png,image/jpeg,image/webp,image/gif',
  acceptedLabel: 'PNG, JPG, WebP or GIF',
} as const;

export interface FeatureDraft {
  id?: number;
  /** Stable client-side key so React and dnd-kit can track a row through reorders. */
  clientKey: string;
  title: string;
  description: string;
  learnMoreUrl: string | null;
  sortOrder: number;
}

export interface FeaturePayload {
  id?: number;
  title: string;
  description: string;
  learnMoreUrl: string | null;
  sortOrder: number;
}

let keyCounter = 0;

/** Unique per session; never sent to the server. */
export function nextClientKey(): string {
  keyCounter += 1;
  return `feature-${Date.now().toString(36)}-${keyCounter}`;
}

export function newFeatureDraft(sortOrder: number): FeatureDraft {
  return { clientKey: nextClientKey(), title: '', description: '', learnMoreUrl: null, sortOrder };
}

/** A row is saved only when both title and description have content. */
export function isSaveableFeature(f: Pick<FeatureDraft, 'title' | 'description'>): boolean {
  return Boolean(f.title.trim() && f.description.trim());
}

/** Drop empty rows, renumber sortOrder 0-based from array position, strip client-only fields. */
export function normalizeFeaturesForSave(features: FeatureDraft[]): FeaturePayload[] {
  return features
    .filter(isSaveableFeature)
    .map(({ clientKey: _clientKey, ...rest }, idx) => ({ ...rest, sortOrder: idx }));
}

export type HeroImageFitStatus = 'ok' | 'wrong-ratio' | 'too-small';

export interface HeroImageFit {
  status: HeroImageFitStatus;
  width: number;
  height: number;
  message: string | null;
}

const RATIO_TOLERANCE = 0.03;

/** Compare an image's natural size to the banner target. Warns, never blocks. */
export function checkHeroImageFit(width: number, height: number): HeroImageFit {
  if (width <= 0 || height <= 0) {
    return { status: 'ok', width, height, message: null };
  }
  const ratio = width / height;
  const drift = Math.abs(ratio - HERO_IMAGE.ratio) / HERO_IMAGE.ratio;
  if (drift > RATIO_TOLERANCE) {
    const trimmed = ratio < HERO_IMAGE.ratio ? 'top and bottom' : 'left and right edges';
    return {
      status: 'wrong-ratio',
      width,
      height,
      message: `This image is ${width} × ${height} px. Members see a ${HERO_IMAGE.ratioLabel} banner, so its ${trimmed} will be cropped. The preview shows exactly what they will see.`,
    };
  }
  if (width < HERO_IMAGE.width) {
    return {
      status: 'too-small',
      width,
      height,
      message: `This image is ${width} × ${height} px, smaller than the recommended ${HERO_IMAGE.width} × ${HERO_IMAGE.height}. It may look soft on high-resolution screens.`,
    };
  }
  return { status: 'ok', width, height, message: null };
}
